import { getChannel } from '@ytl/common-web';
import { getAnalytics, InitParams } from '../../../../modules/verificationComponent/VerificationComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { verificatorResource } from '../../../resource/verificator.resource';
import { emitProcessPhase, emitWidgetEvent } from '../../../utils/emitData';
import { formatMsisdn } from '../../../utils/phoneNumberFormatter';

type FinishParams = {
  email: string;
  phoneNumber: string;
  initParams?: InitParams | null;
  sessionId?: string;
  corrId?: string;
  enableAnalytics?: boolean;
  isWebView?: boolean;
};

export const finishVerification = async ({
  email,
  phoneNumber,
  initParams,
  sessionId,
  corrId,
  enableAnalytics = false,
  isWebView = false,
}: FinishParams): Promise<void> => {
  const borgAnalytics = getAnalytics(enableAnalytics);

  emitProcessPhase('Finish');

  if (initParams?.createDUP) {
    try {
      const res = await verificatorResource.createDUPEntry({ sessionId }, corrId);

      // Check for HTTP error status codes (4xx and 5xx)
      if (res.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'createDUPEntry',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${res.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'createDUPEntry',
              details: `HTTP Error ${res.status}`,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        return;
      }

      // Check for application-level errors in response data
      if (res.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'createDUPEntry',
          action: 'TechnicalError',
          result: 'false',
          reason: `${res.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'createDUPEntry',
              details: res.data,
            },
            isSuccess: false,
          },
          { sessionId: sessionId || '' },
        );
        return;
      }

      console.log('createDUPEntry response:', res);
      const tmsdata = {
        status: res.data.status,
        message: res.data.message,
        dupId: res.data.dupId,
        email: res.data.email,
        msisdn: res.data.msisdn,
      };

      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'Registration success',
        action: 'ProcessFinished',
        result: 'true',
      });
      // Success case
      emitWidgetEvent(
        {
          eventName: WidgetEvent.ProcessFinished,
          eventValue: {
            email: email,
            msisdn: formatMsisdn(phoneNumber),
            tmsdata: tmsdata,
          },
          isSuccess: true,
        },
        { sessionId: sessionId || '' },
      );
    } catch (error) {
      console.error('Failed to create DUP entry:', error);
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'createDUPEntry',
        action: 'TechnicalError',
        result: 'false',
        reason: `${error}`,
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.TechnicalError,
          eventValue: {
            process: 'createDUPEntry',
            details: error,
          },
          isSuccess: false,
        },
        { sessionId: sessionId || '' },
      );
    }
  } else {
    borgAnalytics.sendRegistrationEvent({
      channel: getChannel(isWebView),
      virtualName: 'Registration success',
      action: 'ProcessFinished',
      result: 'true',
    });
    emitWidgetEvent(
      {
        eventName: WidgetEvent.ProcessFinished,
        eventValue: {
          email: email,
          msisdn: formatMsisdn(phoneNumber),
        },
        isSuccess: true,
      },
      { sessionId: sessionId || '' },
    );
  }
};
